import managerFleetService from "@/services/mrfiktiv/services/fleet/managerFleetService";
import { MrfiktivFleetViewModelGen } from "@/services/mrfiktiv/v1/data-contracts";
import store from "@/store/VuexPlugin";
import { Action, Module, Mutation, VuexModule, getModule } from "vuex-module-decorators";
import { buildFleetTree, FleetTreeNode } from "@/lib/build-fleet-tree";
import { UserModule } from "../me-user.store";

@Module({
  dynamic: true,
  namespaced: true,
  name: "fleetManagerStore",
  store
})
export class FleetManagerStore extends VuexModule {
  // First load flag
  private _initialLoad: boolean =
    JSON.parse(window.localStorage.getItem("fleetManagerStore") || "{}").state?._initialLoad ?? false;

  // Fleet tree
  private _fleetTree: FleetTreeNode[] = [];

  get fleetTree(): FleetTreeNode[] {
    return this._fleetTree;
  }

  @Action
  async getFleetTree(data: { partnerId: string; fleetId: string }): Promise<MrfiktivFleetViewModelGen[]> {
    const response = await managerFleetService.get(data.partnerId, data.fleetId);

    const tree = buildFleetTree(response.data);

    this.context.commit("_mutateSelectedFleetId", data.fleetId);

    this.context.commit("_mutateFleetTree", tree);

    if (!this._selectedFleetIds[data.fleetId]) {
      this.context.commit("_mutateSelectedFleetIds", { fleetId: data.fleetId, ids: response.data.map((f) => f.id) });
    }

    return response.data;
  }

  @Mutation
  _mutateFleetTree(tree: FleetTreeNode[]) {
    this._fleetTree = tree;
  }

  // Selected fleetIds
  private _selectedFleetIds: Record<string, string[]> =
    JSON.parse(window.localStorage.getItem("fleetManagerStore") || "{}").state?._selectedFleetIds ?? {};

  get selectedFleetIds() {
    if (!this._selectedFleetId) return [];
    return this._selectedFleetIds[this._selectedFleetId] ?? [];
  }

  @Action
  setSelectedFleetIds(ids: string[]): void {
    if (!this._selectedFleetId) return;
    this.context.commit("_mutateSelectedFleetIds", { fleetId: this._selectedFleetId, ids });
  }

  @Mutation
  _mutateSelectedFleetIds(payload: { fleetId: string; ids: string[] }) {
    this._selectedFleetIds = { ...this._selectedFleetIds, [payload.fleetId]: payload.ids };
  }

  @Mutation
  _setInitialLoad(value: boolean) {
    this._initialLoad = value;
  }

  // Selected parent fleetId
  private _selectedFleetId: string | undefined =
    JSON.parse(window.localStorage.getItem("fleetManagerStore") || "{}").state?._selectedFleetId ?? undefined;

  get selectedFleetId(): string | undefined {
    return this._selectedFleetId;
  }

  get selectedFleet(): MrfiktivFleetViewModelGen | null {
    return this.fleets.find((f) => f.id === this.selectedFleetId) ?? null;
  }

  @Action
  async setSelectedFleetId(data: { fleetId: string; partnerId: string }) {
    const { fleetId, partnerId } = data;

    const response = await managerFleetService.get(partnerId, fleetId);
    const tree = buildFleetTree(response.data);

    this.context.commit("_mutateSelectedFleetId", fleetId);

    this.context.commit("_mutateFleetTree", tree);

    if (!this._selectedFleetIds[fleetId]) {
      this.context.commit("_mutateSelectedFleetIds", { fleetId, ids: response.data.map((f) => f.id) });
    }

    return response.data;
  }

  @Mutation
  _mutateSelectedFleetId(fleetId: string) {
    this._selectedFleetId = fleetId;
  }

  // All parent fleets
  private _fleets: MrfiktivFleetViewModelGen[] = [];

  get fleets(): MrfiktivFleetViewModelGen[] {
    return this._fleets;
  }

  get permissionFleets() {
    return this.fleets.filter((f) => UserModule.distinctFleetIds.includes(f.id));
  }

  @Action
  async loadParentFleets(data: { partnerId: string; fleetIds: string[] }) {
    const allFleets: MrfiktivFleetViewModelGen[] = [];
    const batchSize = 5;

    for (let i = 0; i < data.fleetIds.length; i += batchSize) {
      const batch = data.fleetIds.slice(i, i + batchSize);

      const batchPromises = batch.map((fleetId) => managerFleetService.get(data.partnerId, fleetId));

      const batchResponses = await Promise.all(batchPromises);

      for (const response of batchResponses) {
        allFleets.push(...response.data);
      }
    }

    this.context.commit("_mutateFleets", allFleets);

    return allFleets;
  }

  @Mutation
  _mutateFleets(fleets: MrfiktivFleetViewModelGen[]) {
    this._fleets = fleets;
  }

  @Action
  reset() {
    this.context.commit("_mutateSelectedFleetIds", { fleetId: "", ids: [] });
    this.context.commit("_mutateFleets", []);
    this.context.commit("_mutateFleetTree", []);
    this.context.commit("_mutateSelectedFleetId", undefined);
    this.context.commit("_setInitialLoad", false);
  }
}

export const FleetManagerModule = getModule(FleetManagerStore);
